import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { serviceMainCategoriesApi } from '../../services/smartBillApi';
import './ServicesManagement.css';

const UNIT_SUGGESTIONS = ['piece', 'ltr', 'kg', 'g', 'ml', 'box', 'pack', 'set', 'hour', 'job'];

const MainCategoriesManagement = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '',
    kind: 'product',
    default_unit: 'piece',
    is_active: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await serviceMainCategoriesApi.ensureMiscellaneous().catch(() => null);
      const res = await serviceMainCategoriesApi.getAll({
        search: search.trim() || undefined,
        kind: kindFilter === 'all' ? undefined : kindFilter,
        includeInactive: true,
      });
      setRows(res.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load main categories');
    } finally {
      setLoading(false);
    }
  }, [search, kindFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = (kind = 'product') => {
    setEditing(null);
    setForm({
      name: '',
      kind,
      default_unit: kind === 'product' ? 'piece' : '',
      is_active: true,
    });
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      kind: row.kind || 'service',
      default_unit: row.default_unit || '',
      is_active: row.is_active !== false,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      const body = {
        name: form.name.trim(),
        kind: form.kind,
        default_unit: form.default_unit.trim() || null,
        is_active: form.is_active,
      };
      if (editing) {
        await serviceMainCategoriesApi.update(editing.id, body);
      } else {
        await serviceMainCategoriesApi.create(body);
      }
      setShowModal(false);
      load();
    } catch (err) {
      alert(err.message || 'Save failed');
    }
  };

  const handleDeactivate = async (row) => {
    if (!window.confirm(`Deactivate "${row.name}"? Row is kept (no delete).`)) return;
    try {
      await serviceMainCategoriesApi.deactivate(row.id);
      load();
    } catch (err) {
      alert(err.message || 'Deactivate failed');
    }
  };

  return (
    <div className="services-management">
      <div className="page-header">
        <div>
          <h1>Product / Service Group Management</h1>
          <p style={{ margin: '6px 0 0', opacity: 0.85 }}>
            Groups for products &amp; services — shared with Mitra Operate (bills &amp; purchases)
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Link to="/bills/inventory" className="btn btn-secondary">
            Inventory hub
          </Link>
          <Link to="/bills/services" className="btn btn-secondary">
            Products / Services
          </Link>
          <button type="button" className="btn btn-primary" onClick={() => openCreate('product')}>
            + Product category
          </button>
          <button type="button" className="btn btn-primary" onClick={() => openCreate('service')}>
            + Service category
          </button>
        </div>
      </div>

      <div className="filters-bar">
        <input
          type="search"
          placeholder="Search main category or unit…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
          <option value="all">All types</option>
          <option value="product">Product</option>
          <option value="service">Service</option>
        </select>
        <button type="button" className="btn btn-secondary" onClick={load}>
          Refresh
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {loading ? (
        <p>Loading…</p>
      ) : (
        <div className="table-wrap">
          <table className="services-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Default unit</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{row.name}</strong>
                  </td>
                  <td>
                    <span className={row.kind === 'product' ? 'badge-success' : 'badge-info'}>
                      {row.kind}
                    </span>
                  </td>
                  <td>{row.default_unit || '—'}</td>
                  <td>
                    {row.is_active !== false ? (
                      <span className="badge-success">Active</span>
                    ) : (
                      <span className="badge-danger">Inactive</span>
                    )}
                  </td>
                  <td>
                    <button type="button" className="btn-link" onClick={() => openEdit(row)}>
                      Edit
                    </button>
                    <Link
                      className="btn-link"
                      to={`/bills/services?mainCategoryId=${row.id}&itemKind=${row.kind}`}
                      style={{ marginLeft: 8 }}
                    >
                      Items
                    </Link>
                    {row.is_active !== false &&
                      String(row.name).toLowerCase() !== 'miscellaneous' && (
                        <button
                          type="button"
                          className="btn-link"
                          style={{ marginLeft: 8, color: '#b91c1c' }}
                          onClick={() => handleDeactivate(row)}
                        >
                          Deactivate
                        </button>
                      )}
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: 24 }}>
                    No categories yet. Create a product category (e.g. Soaps) or service category
                    (e.g. Online Services).
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-content" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Edit group' : 'Add group'}</h2>
              <button type="button" className="modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="service-form">
              <div className="form-group">
                <label>Type *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['product', 'service'].map((k) => (
                    <button
                      key={k}
                      type="button"
                      className={`btn ${form.kind === k ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          kind: k,
                          default_unit: k === 'product' && !f.default_unit ? 'piece' : f.default_unit,
                        }))
                      }
                    >
                      {k === 'product' ? 'Product' : 'Service'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label>Category name *</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder={form.kind === 'product' ? 'e.g. Soaps, Oils' : 'e.g. Print Services, Online Services'}
                />
              </div>
              <div className="form-group">
                <label>Default unit {form.kind === 'product' ? '*' : '(optional)'}</label>
                <input
                  list="unit-suggestions"
                  value={form.default_unit}
                  onChange={(e) => setForm((f) => ({ ...f, default_unit: e.target.value }))}
                  placeholder="piece / ltr / kg…"
                  required={form.kind === 'product'}
                />
                <datalist id="unit-suggestions">
                  {UNIT_SUGGESTIONS.map((u) => (
                    <option key={u} value={u} />
                  ))}
                </datalist>
              </div>
              {editing && (
                <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  />
                  Active
                </label>
              )}
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainCategoriesManagement;
